import React, { useState, useEffect, useContext } from "react";
import { TodoItem } from "../common/TodoItem";
import { codes as supportedCodes } from "../../configs/supportedResponseCodes.json";
import {
	getCompletedResponseSet,
	getDueResponseSet,
	getMissedResponseSet,
	getUpcomingResponseSet,
	getNurseToDoResponseSet,
	getSettingsCompletionStatus,
	getDueResponseCount,
} from "../../services/dashApiService";
import { hasInternetConnection } from "../../services/systemService";
import { toast } from "react-toastify";
import { TransitionSlideUp } from "../transitions/TransitionSlideUp";
import { TransitionConfig } from "../../configs/transitionConfig";
import { LoadingSpinnerAlt } from "../common/LoadingSpinnerAlt";
import { toastConfig } from "../../configs/toastConfig";
import { DashboardContext } from "../../contexts/dashboardContext";

const actionLabelMap = {
	due: "Due by",
	upcoming: "Upcoming",
	missed: "Missed",
	completed: "Complete",
	nursetodo: "Nurse Only",
};

const actionFunctionMap = {
	"Due by": getDueResponseSet,
	Upcoming: getUpcomingResponseSet,
	Complete: getCompletedResponseSet,
	Missed: getMissedResponseSet,
	"Nurse Only": getNurseToDoResponseSet,
};

export const TodoList = ({ actions, showNurseToDo }) => {
	const [responseSet, setResponseSet] = useState([]);
	const [hasErrored, setHasErrored] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [isShowing, setIsShowing] = useState(false);
	const [isShowingTimer, setIsShowingTimer] = useState(0);
	const [setupCompletion, setSetupCompletion] = useState(null);

	const { setDueReportsCount } = useContext(DashboardContext);

	const getNoReportText = () => {
		let ret;
		let setup;
		const completedSections = setupCompletion?.completedSections || 0;
		const totalSections = setupCompletion?.totalSections || 0;

		switch (true) {
			case setupCompletion == null:
				setup = <></>;
				break;

			case completedSections === 0:
				setup = (
					<>
						<p className="mt-2 md:mt-6">
							You haven't started to record your Setup data.
						</p>
						<p className="mt-2">
							This data is important - please complete this as soon as you are
							able.
						</p>
					</>
				);
				break;

			case completedSections >= totalSections:
				setup = <></>;
				break;

			default:
				setup = (
					<>
						<p className="mt-2 md:mt-6">{`You have completed ${completedSections} of ${totalSections} sections of your Setup data.`}</p>
						<p className="mt-2">
							Please complete the rest as soon as possible.
						</p>
					</>
				);
				break;
		}

		switch (true) {
			case actions.indexOf("Upcoming") >= 0:
				ret = (
					<>
						<p className="mb-2">You don't have any upcoming reports.</p>
						<p className="mb-2">
							This is a temporary problem with our systems.
						</p>
						<p>Our support team will rectify this as soon as possible.</p>
					</>
				);
				break;

			case actions.indexOf("Due by") >= 0:
				ret = (
					<>
						<p className="mb-2">
							You currently don't have any reports to complete.
						</p>
						<p>Check your Upcoming reports to see when the next one is due.</p>
						{setup}
					</>
				);
				break;

			case actions.indexOf("Complete") >= 0:
			case actions.indexOf("Missed") >= 0:
				ret = (
					<>
						<p className="mb-2">You haven't yet completed any reports.</p>
						<p>
							When you have, they will appear here
							<br />
							and you will be able to review them in Your Summary.
						</p>
					</>
				);
				break;
			case actions.indexOf("nurseToDo") >= 0:
				ret = <p>No registrations to display</p>;
				break;
			default:
				ret = <p>No reports to display</p>;
				break;
		}

		return ret;
	};

	const handleStartLoading = () => {
		setHasErrored(false);
		setIsLoading(true);
		setIsShowing(false);
	};

	const handleFinishLoading = () => {
		setTimeout(() => {
			setIsShowingTimer(0);
			setIsShowing(true);
		}, TransitionConfig.slideYTransition.enterDuration);

		setIsLoading(false);
	};

	const handleError = (e) => {
		setIsLoading(false);
		setHasErrored(true);
	};

	useEffect(() => {
		getSettingsCompletionStatus()
			.then((data) => {
				setSetupCompletion({
					completed: data.data.complete,
					totalSections: data.data.totalSections,
					completedSections: data.data.completedSections,
				});
			})
			.catch((err) => {
				console.log(err);
			});

		return () => {
			if (isShowingTimer) {
				clearTimeout(isShowingTimer);
			}
		};
	}, [isShowingTimer]);

	useEffect(() => {
		handleStartLoading();

		if (hasInternetConnection()) {
		} else {
			toast.error(
				<>
					<p className="mb-1">There is a problem connecting to the internet.</p>
					<p className="mb-1">
						If you have been entering data it may have been lost and you might
						have to re-enter it.
					</p>
					<p className="mb-1">
						Please check your internet connection and try again. If the problem
						persists, contact our helpdesk, quoting reference '#TD001'.
					</p>
				</>,
				{
					toastId: toastConfig.toastIds.errNetwork,
				}
			);
			handleError("No internet connection available");
			return;
		}

		const promises = actions.map((action) => {
			if (action === "Nurse Only" && !showNurseToDo) {
				return null;
			}

			const getFunc = actionFunctionMap[action];

			if (getFunc) {
				return getFunc(true);
			} else {
				return null;
			}
		});

		Promise.all(promises)
			.then((responses) => {
				const values = [];
				responses.forEach((response) => {
					const filtered = response.data.filter((e) =>
						supportedCodes.some((w) => w.code === e.questionnaireCode)
					);
					const mappedWithAction = filtered.map((e) => {
						const action = actionLabelMap[response.status];

						return { todo: e, action };
					});
					values.push(...mappedWithAction);
				});

				setResponseSet(values);

				handleFinishLoading();
			})
			.catch((error) => {
				handleError(error);
			});
	}, [actions, showNurseToDo]);

	useEffect(() => {
		getDueResponseCount()
			.then((data) => {
				setDueReportsCount(data.data);
			})
			.catch(() => {});
	}, [setDueReportsCount]);

	if (hasErrored === true) {
		return (
			<>
				{/* {nurseActions()} */}
				<p className={"text-center text-black p-3"}>
					We could not retrieve your questionnaires at this time.
					<br />
					Please try again later.
				</p>
			</>
		);
	}

	// work out what we need to return
	// isLoading -> show the spinner
	// isShowing && todo list length === 0 -> show empty todo list message
	// isShowing && todo list length > 0 -> show todo list

	if (isLoading) {
		return <LoadingSpinnerAlt isFixed={true} />;
	}

	const noReportText = getNoReportText();

	return (
		<>
			<TransitionSlideUp
				show={isShowing}
				enterTime={TransitionConfig.slideYTransition.enterDuration}
				leaveTime={TransitionConfig.slideYTransition.leaveDuration}
			>
				{/* {nurseActions()} */}
				{responseSet.length === 0 && (
					<div className={"text-center text-black p-3"}>{noReportText}</div>
				)}
				{responseSet.length > 0 && (
					<ul>
						{responseSet.map((e) => {
							const { todo, action } = e;
							return (
								<li key={todo.id}>
									<TodoItem
										id={todo.id}
										code={todo.questionnaireCode}
										locked={todo.locked}
										title={todo.title}
										action={action}
										actionLabel={
											action === "Due by" ? todo.expectedDuration : null
										}
										dueDate={todo.dueDate}
										questionnaires={
											todo.questionnaires ? todo.questionnaires : null
										}
										numQuestionnaires={
											todo.questionnaires ? todo.questionnaires.length : 0
										}
										responseHistoryExists={todo.responseHistory ? true : false}
										lastSubmissionDate={todo.lastSubmissionDate}
									/>
								</li>
							);
						})}
					</ul>
				)}
			</TransitionSlideUp>
		</>
	);
};
